// src/components/login/mfa-verify-page.tsx
import { useState, FormEvent } from "react";
import { authApi } from "@/api/auth";
import { UpmodeLogo } from "./upmode-logo";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Alert, AlertDescription } from "../ui/alert";
import {
  ShieldCheck, AlertCircle, Loader2, KeyRound, ArrowLeft,
} from "lucide-react";

interface MfaVerifyPageProps {
  challengeToken: string;
  username: string;
  onVerifySuccess: (
    accessToken: string,
    payload: {
      username: string;
      is_admin: boolean;
      user_type: string;
      full_name?: string | null;
      backup_code_used: boolean;
      backup_codes_remaining: number;
    },
  ) => void;
  onCancel: () => void;
}

export function MfaVerifyPage({
  challengeToken,
  username,
  onVerifySuccess,
  onCancel,
}: MfaVerifyPageProps) {
  const [mode, setMode]   = useState<"totp" | "backup">("totp");
  const [code, setCode]   = useState("");
  const [errorMsg, setErrorMsg]   = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErrorMsg("");

    const trimmed = code.trim();
    if (mode === "totp") {
      const cleaned = trimmed.replace(/\D/g, "");
      if (cleaned.length !== 6) {
        setErrorMsg("Enter the 6-digit code from your authenticator app.");
        return;
      }
    } else if (trimmed.length < 6) {
      setErrorMsg("Enter your backup code (looks like XXXX-XXXX).");
      return;
    }

    setIsLoading(true);
    try {
      const res = await authApi.mfaVerify(
        challengeToken,
        mode === "totp" ? trimmed.replace(/\D/g, "") : trimmed,
        mode === "backup",
      );

      onVerifySuccess(res.access_token, {
        username:               res.username,
        is_admin:               res.is_admin,
        user_type:              res.user_type || "basic",
        full_name:              res.full_name,
        backup_code_used:       res.backup_code_used,
        backup_codes_remaining: res.backup_codes_remaining,
      });
    } catch (e: any) {
      const msg = String(e?.message || "");
      if (/lock/i.test(msg) || /429/.test(msg)) {
        setErrorMsg("Too many failed attempts. Try again in 15 minutes.");
      } else if (/invalid code/i.test(msg) || /401/.test(msg)) {
        setErrorMsg(
          mode === "totp"
            ? "That code didn't match. Try the latest 6-digit code from your app."
            : "Backup code didn't match. Each code can only be used once.",
        );
      } else {
        setErrorMsg(msg || "Verification failed. Try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "var(--background)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "clamp(16px, 3vh, 28px) clamp(16px, 3vw, 24px)",
        position: "relative", overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          position: "absolute", inset: 0,
          background: `
            radial-gradient(circle at 20% 20%, var(--spectral-violet-5) 0%, transparent 50%),
            radial-gradient(circle at 80% 80%, var(--signal-blue-5) 0%, transparent 50%)
          `,
          opacity: 0.6, pointerEvents: "none",
        }}
      />

      <div style={{
        width: "100%", maxWidth: 440,
        display: "flex", flexDirection: "column", gap: 22,
        position: "relative", zIndex: 1,
      }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ display: "inline-flex", marginBottom: 14 }}>
            <UpmodeLogo />
          </div>
          <h1 style={{
            fontFamily: "var(--font-sans)",
            fontSize: "var(--text-3xl)",
            fontWeight: "var(--font-weight-semibold)",
            margin: 0, color: "var(--foreground)",
            letterSpacing: "-0.02em",
          }}>
            Two-factor authentication
          </h1>
          <p style={{
            marginTop: 8, fontSize: "var(--text-sm)",
            color: "var(--muted-foreground)",
          }}>
            Signing in as <strong>{username}</strong>
          </p>
        </div>

        <div style={{
          backgroundColor: "var(--card)",
          border: "1px solid var(--border)",
          borderRadius: 16,
          padding: "clamp(22px, 3.5vh, 36px)",
          boxShadow: "var(--elevation-lg)",
          position: "relative", overflow: "hidden",
        }}>
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, height: 4,
            background: "linear-gradient(90deg, var(--spectral-violet), var(--signal-blue), var(--alpha-teal))",
            borderRadius: "16px 16px 0 0", opacity: 0.8,
          }}/>

          {errorMsg && (
            <Alert variant="destructive" style={{
              marginBottom: 14,
              backgroundColor: "var(--infrared-coral-5)",
              border: "1px solid var(--infrared-coral)",
            }}>
              <AlertCircle style={{ width: 16, height: 16, color: "var(--infrared-coral)" }} />
              <AlertDescription style={{ color: "var(--infrared-coral)" }}>{errorMsg}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{
              display: "flex", alignItems: "center", gap: 10, marginBottom: 16,
              color: "var(--foreground)",
            }}>
              {mode === "totp"
                ? <ShieldCheck style={{ width: 20, height: 20, color: "var(--alpha-teal)" }} />
                : <KeyRound style={{ width: 20, height: 20, color: "var(--alpha-teal)" }} />}
              <p style={{ margin: 0, fontSize: "var(--text-sm)" }}>
                {mode === "totp"
                  ? "Enter the 6-digit code from your authenticator app."
                  : "Enter one of your backup codes (XXXX-XXXX)."}
              </p>
            </div>

            <Input
              type="text"
              inputMode={mode === "totp" ? "numeric" : "text"}
              pattern={mode === "totp" ? "[0-9]*" : undefined}
              maxLength={mode === "totp" ? 6 : 10}
              value={code}
              onChange={(e) => {
                setCode(mode === "totp" ? e.target.value.replace(/\D/g, "") : e.target.value);
              }}
              disabled={isLoading}
              placeholder={mode === "totp" ? "123 456" : "ABCD-1234"}
              autoFocus
              autoComplete="one-time-code"
              style={{
                height: 56,
                fontSize: 24,
                textAlign: "center",
                letterSpacing: "0.4em",
                fontFamily: "var(--font-mono, monospace)",
                borderRadius: "var(--radius)",
                marginBottom: 16,
                textTransform: mode === "backup" ? "uppercase" : "none",
              }}
            />

            <Button
              type="submit"
              disabled={isLoading || !code.trim()}
              style={{
                width: "100%", height: 48,
                background: !isLoading && code.trim() ? "var(--alpha-teal)" : "var(--muted)",
                color: "white", fontWeight: 600,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}
            >
              {isLoading
                ? <Loader2 className="animate-spin" style={{ width: 18, height: 18 }} />
                : <ShieldCheck style={{ width: 18, height: 18 }} />}
              Verify
            </Button>
          </form>

          <div style={{
            marginTop: 18, paddingTop: 14,
            borderTop: "1px solid var(--border)",
            display: "flex", justifyContent: "space-between", alignItems: "center",
            gap: 8, flexWrap: "wrap",
          }}>
            <button
              type="button"
              onClick={() => { setMode(mode === "totp" ? "backup" : "totp"); setCode(""); setErrorMsg(""); }}
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: "var(--signal-blue)",
                fontSize: "var(--text-sm)",
                fontWeight: 600,
                padding: 0,
              }}
            >
              {mode === "totp" ? "Use a backup code instead" : "Use authenticator app instead"}
            </button>

            <button
              type="button"
              onClick={onCancel}
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: "var(--muted-foreground)",
                fontSize: "var(--text-sm)",
                display: "flex", alignItems: "center", gap: 4,
                padding: 0,
              }}
            >
              <ArrowLeft style={{ width: 14, height: 14 }} />
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}